import { useState } from 'react';
import type { FormTemplate } from '@worksy360/shared';
import { api, ApiRequestError } from '../lib/api.js';
import { Button, Dialog, Field, Input } from '../ui/index.js';
import { useToast } from '../ui/toast.js';

/** Fill and submit a form/checklist instance for a job (Parts 46). */
export function FormFill({ template, customerId, jobId, onClose, onDone }: { template: FormTemplate; customerId: string; jobId: string; onClose: () => void; onDone: () => void }) {
  const toast = useToast();
  const [values, setValues] = useState<Record<string, string>>({});
  const [errs, setErrs] = useState<Record<string, string>>({});
  const [saving, setSaving] = useState(false);
  const set = (id: string, v: string) => setValues((s) => ({ ...s, [id]: v }));

  const submit = async () => {
    setErrs({}); setSaving(true);
    try {
      await api.post(`/customers/${customerId}/jobs/${jobId}/forms`, { templateId: template.id, jobId, values, completed: true });
      toast.push(`${template.name} saved`, 'success');
      onDone();
    } catch (err) {
      if (err instanceof ApiRequestError) { setErrs(err.fields ?? {}); toast.push(err.message, 'error'); }
    } finally { setSaving(false); }
  };

  return (
    <Dialog title={template.name} onClose={onClose}
      footer={<><Button variant="secondary" onClick={onClose}>Cancel</Button><Button loading={saving} onClick={submit}>Save form</Button></>}>
      <div className="stack gap-4">
        {template.fields.map((f) => (
          <Field key={f.id} label={f.label + (f.required ? ' *' : '')} error={errs[f.id]} hint={f.helpText}>
            {f.type === 'passfail' ? (
              <div className="row gap-2">
                {(['pass', 'fail', 'na'] as const).map((o) => (
                  <button key={o} type="button" className={`btn ${values[f.id] === o ? 'btn-primary' : 'btn-secondary'} btn-sm`} onClick={() => set(f.id, o)}>{o.toUpperCase()}</button>
                ))}
              </div>
            ) : f.type === 'checkbox' ? (
              <label className="row gap-2"><input type="checkbox" checked={values[f.id] === 'true'} onChange={(e) => set(f.id, e.target.checked ? 'true' : 'false')} /> Yes</label>
            ) : f.type === 'select' ? (
              <select className="select" value={values[f.id] ?? ''} onChange={(e) => set(f.id, e.target.value)}>
                <option value="">Choose…</option>
                {f.options.map((o) => <option key={o} value={o}>{o}</option>)}
              </select>
            ) : f.type === 'photo' || f.type === 'signature' ? (
              <Input placeholder={`${f.type} capture (upload wired with S3 presign)`} value={values[f.id] ?? ''} onChange={(e) => set(f.id, e.target.value)} />
            ) : (
              <Input type={f.type === 'number' ? 'number' : f.type === 'date' ? 'date' : 'text'} value={values[f.id] ?? ''} onChange={(e) => set(f.id, e.target.value)} />
            )}
          </Field>
        ))}
      </div>
    </Dialog>
  );
}
